import { useMemo } from "react";
import type { DiscoverItem, DiscoverSavedAdapter } from "@majourneys/discover";
import { useSaved } from "../../saved/state/saved-context";
import { useSavedStays } from "../../saved/state/saved-stays";

/**
 * Discover's hearts and its Liked section, on the site's one saved list.
 *
 * Discover used to keep a list of its own in the browser, so /liked never
 * showed what a guest hearted anywhere else (or what their account held). A
 * hotel goes to the saved-stays list, everything else (camps included) to the
 * activity list, the same split the card hearts make.
 */
export function isStayItem(item: DiscoverItem): boolean {
  return !!item.detailHref && item.detailHref.includes("/stays/");
}

/** The stay's id: Discover prefixes its ids with "mj-". */
function stayId(item: DiscoverItem): string {
  return item.id.startsWith("mj-") ? item.id.slice(3) : item.id;
}

export function useDiscoverSaved(): DiscoverSavedAdapter {
  const activities = useSaved();
  const stays = useSavedStays();
  return useMemo<DiscoverSavedAdapter>(
    () => ({
      isSaved: (item) => (isStayItem(item) ? stays.isSaved(stayId(item)) : activities.isSaved(item.sourceSlug)),
      setSaved: (item, saved) => {
        if (isStayItem(item)) {
          // The stays list only toggles, so only when it would change.
          if (stays.isSaved(stayId(item)) === saved) return;
          void stays.toggle({
            id: stayId(item),
            slug: item.sourceSlug,
            citySlug: item.citySlug ?? "",
            name: item.title,
            cityName: item.location,
            image: item.image || undefined,
          });
          return;
        }
        void activities.setSaved(
          {
            slug: item.sourceSlug,
            title: item.title,
            city: item.location,
            price: item.priceMad ?? 0,
            currency: "MAD",
            image: item.image || undefined,
            category: item.pinLabel || undefined,
          },
          saved,
        );
      },
    }),
    [activities, stays],
  );
}
